/**
 * UnattributedOrdersCard — surfaces Shopify shipping cities that don't
 * resolve to a known TDB outlet. CEO sees new markets (Chennai, Hyderabad,
 * Pune, Kolkata) the moment the first order arrives there, and decides:
 *   • Map to an existing outlet (Bengaluru/Mumbai/Gurgaon)
 *   • Create a new outlet (Chennai as its own tile)
 *   • Leave under Online / B2B
 *
 * Read-only for now — the "Add to map" / "Create outlet" actions log
 * an intent which the CEO executes via the Admin panel. Keeps this card
 * low-risk and informational. Actionable CTAs can be layered on later.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => {
  if (!n) return '₹0';
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const relativeDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso.replace('Z', '+00:00'));
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return '—';
  }
};

export default function UnattributedOrdersCard({ divisionId = 'tdb', sinceDays = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token');
    axios.get(`${API}/shopify/unattributed-orders/${divisionId}?since_days=${sinceDays}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ unattributed: [], total_orders: 0, total_revenue: 0 }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [divisionId, sinceDays]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 border border-[#E5E1DC]" data-testid="unattributed-loading">
        <p className="text-xs text-[#6B7280]">Scanning unattributed orders…</p>
      </div>
    );
  }
  const rows = data?.unattributed || [];

  return (
    <div className="bg-white rounded-lg border border-[#E5E1DC] overflow-hidden" data-testid="unattributed-orders-card">
      <div className="px-4 py-3 border-b border-[#E5E1DC] bg-[#FEF3C7]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] tracking-widest uppercase text-[#92400E]">Unattributed Orders · last {sinceDays}d</p>
            <p className="text-sm font-semibold text-[#92400E] mt-0.5">
              New cities Shopify is serving — not yet mapped to an outlet
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#92400E]">Total</p>
            <p className="text-lg font-bold text-[#92400E]">{fmt(data?.total_revenue || 0)}</p>
            <p className="text-[10px] text-[#92400E]">{data?.total_orders || 0} orders</p>
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-[#2D8B5E] font-medium">✓ All shipping cities resolved to a known outlet.</p>
          <p className="text-[11px] text-[#6B7280] mt-1">
            The moment an order lands from a new city (Chennai, Hyderabad, Pune…) it will show up here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="unattributed-table">
            <thead>
              <tr className="bg-[#F7F5F2] text-[10px] uppercase tracking-wide text-[#6B7280]">
                <th className="text-left px-3 py-2 font-medium">Shopify city string</th>
                <th className="text-right px-3 py-2 font-medium">Orders</th>
                <th className="text-right px-3 py-2 font-medium">Revenue</th>
                <th className="text-right px-3 py-2 font-medium">First seen</th>
                <th className="text-right px-3 py-2 font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {rows.map((r) => (
                <tr key={r.city} data-testid={`unattributed-row-${r.city}`}>
                  <td className="px-3 py-2 font-medium text-[#1B2A4A]">{r.city}</td>
                  <td className="px-3 py-2 text-right text-[#1B2A4A]">{r.orders}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#1B2A4A]">{fmt(r.revenue)}</td>
                  <td className="px-3 py-2 text-right text-xs text-[#6B7280]">{relativeDate(r.first_seen)}</td>
                  <td className="px-3 py-2 text-right text-xs text-[#6B7280]">{relativeDate(r.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-4 py-2 text-[11px] text-[#6B7280] bg-[#FAFAF8] border-t border-[#E5E1DC]">
        💡 Go to <span className="font-semibold text-[#1B2A4A]">Admin → Revenue Channels</span> to rename/merge channels, or <span className="font-semibold text-[#1B2A4A]">Admin → Outlets → Add Outlet</span> to create a new city (e.g. Chennai).
      </div>
    </div>
  );
}
